import { isEmpty } from "lodash";
import { KeyValuePairs } from "../lambda/types";

export const convertListToMap = (inputList: string[]): { [listElement: string]: boolean } => {
    const outputMap: { [listElement: string]: boolean } = {};
    inputList.forEach((input) => {
        outputMap[input] = true;
    });

    return outputMap;
};

export const convertSetToMap = (inputList: Set<string>): { [listElement: string]: boolean } => {
    const outputMap: { [listElement: string]: boolean } = {};
    inputList.forEach((input) => {
        outputMap[input] = true;
    });

    return outputMap;
};

export const roundNumber = (num: number, decimalPlaces = 100): number => {
    return Math.round((num + Number.EPSILON) * decimalPlaces) / decimalPlaces;
};

export const roundOptionalNumber = (num?: number, decimalPlaces = 100): number | undefined => {
    if (!num) {
        return undefined;
    }
    return Math.round((num + Number.EPSILON) * decimalPlaces) / decimalPlaces;
};

export function getGrammaticallyNumberizedText(singularText: string, pluralText: string, count: number): string {
    return count === 1 ? singularText : pluralText;
}

export const swapKeyVal = (json: {[key: string]: string | number}): {[key: string]: string | number} => {
    const swapped: {[key: string]: string | number} = {};
    Object.keys(json).forEach((key) => {
        const val = json[key];
        swapped[val] = key;
    });
    return swapped;
};

export function getRadius(latitudeDelta: number): number {
    return (latitudeDelta * 69) / 2;
};

export function isNotNull<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
}

export function getDaysDifference(date1: string | Date, date2: string | Date): number {
    if (!date1 || !date2) return 0;
    // Convert both inputs to Date objects if they are strings
    const dateObj1 = new Date(date1);
    const dateObj2 = new Date(date2);

    // Get the time difference in milliseconds
    const differenceMs = Math.abs(dateObj1.getTime() - dateObj2.getTime());

    // Convert the difference to days
    return Math.floor(differenceMs / (1000 * 60 * 60 * 24));
}

export function getTimeDifference(date1: string | Date, date2: string | Date): { days: number, hours: number, minutes: number } {
    if (!date1 || !date2) return { days: 0, hours: 0, minutes: 0 };
    // Convert both inputs to Date objects if they are strings
    const dateObj1 = new Date(date1);
    const dateObj2 = new Date(date2);

    const differenceMs = Math.abs(dateObj1.getTime() - dateObj2.getTime());

    const days = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((differenceMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((differenceMs % (1000 * 60 * 60)) / (1000 * 60));

    return { days, hours, minutes };
}

export function getMostSignificantTimeDifference(date1: string | Date, date2: string | Date): string {
    const { days, hours, minutes } = getTimeDifference(date1, date2);

    if (days > 0) {
        return `${days} days`;
    }
    if (hours > 0) {
        return `${hours} hours`;
    }
    return `${minutes} minutes`;
}

export function getMinutesDifference(startTime: string | Date, endTime: string | Date): number | null {
    const start = new Date(startTime);
    const end = new Date(endTime);

    // eslint-disable-next-line no-restricted-globals
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return null;
    }

    const diffMs = Math.abs(end.getTime() - start.getTime());

    const diffMinutes = diffMs / (1000 * 60);
    return diffMinutes;
}
  

export function sliceAndEllipsisGeneric(text: string | null, maxLength = 32): string {
    if(!text) {
        return '';
    }
    if (text?.length > maxLength) {
        // Slice the string to the first 'maxLength' characters and add two dots
        return text.slice(0, maxLength) + '..';
    }
    // Return the original text if it's 'maxLength' characters or less
    return text;
}

/**
 * Generator function to yield items from records lazily
 */
export function* generateItemFromRecords(records: any[]) {
    for (const item of records) {
        yield item;
    }
}

export const convertObjectToArray = (obj: KeyValuePairs<string |number>) => {
    if(isEmpty(obj)) {
        return [];
    }
    return Object.entries(obj).map(([key, value]) => ({
        key,
        value
    }));
};

export const convertArrayToObject = (arr: { key: string, value: string | number }[]) => {
    if (isEmpty(arr)) {
        return {};
    }
    const obj: { [key: string]: any } = {};
    arr.forEach(item => {
        obj[item.key] = item.value;
    });
    return obj;
};


// Checks if a value is null or undefined.
export const isNil = (value: any): boolean => value === null || value === undefined;

// For string: valid if not nil and not an empty string.
export const isValidString = (value: any): boolean => !isNil(value) && value !== '';

// For number: valid if not nil and the value converts to a number that is not NaN.
export const isValidNumber = (value: any): boolean => !isNil(value) && value !== '';

// For boolean: valid if not nil (note: false is valid).
export const isValidBoolean = (value: any): boolean => !isNil(value);

export const isValidEnum = <T extends { [key: string]: unknown } | ArrayLike<unknown>>(value: any, requiredEnum: T): boolean => {
    return Object.values(requiredEnum).includes(value);
};

export const isAlphanumericString = (str: string): boolean => {
    return /^[a-zA-Z0-9]+$/.test(str);
};
